import React from "react";
import {
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
} from "@mui/material";

interface Option {
  value: string | number;
  label: string | number | React.ReactNode;
}
interface SelectOptionsProps {
  options?: Option[];
  label?: string;
  disabled?: boolean;
  value?: string | number | undefined;
  isMandatory: boolean;
  onChange: (value: string | number) => void;
  helperText?: string;
  error?: boolean;
  padding?: string;
  placeholder?: string;
  isApplicant?: boolean;
  lableClass?: string;
}

const SelectOptions: React.FC<SelectOptionsProps> = ({
  options,
  label,
  disabled = false,
  value,
  isMandatory = false,
  onChange,
  helperText,
  error,
  placeholder,
  isApplicant = false,
  lableClass
}) => {
  const [open, setOpen] = React.useState(false);
  const selectRef = React.useRef<HTMLDivElement>(null);
  const toDisplayText = (val: any): string => {
    if (typeof val === "string" || typeof val === "number") return String(val);
    if (val && typeof val === "object") {
      if (typeof val.label === "string" || typeof val.label === "number") {
        return String(val.label);
      }
      if (typeof val.value === "string" || typeof val.value === "number") {
        return String(val.value);
      }
    }
    return "";
  };

  React.useEffect(() => {
    if (!open) return;

    let cleanupFn: (() => void) | undefined;

    const handleScroll = (event: Event) => {
      // Prevent closing if scrolling inside the dropdown menu itself
      const target = event.target;
      
      // Check if target is an HTMLElement with closest method
      if (target && typeof (target as HTMLElement).closest === 'function') {
        const htmlTarget = target as HTMLElement;
        if (htmlTarget.closest('.MuiPopover-root') || htmlTarget.closest('.MuiMenu-paper')) {
          return;
        }
      }
      
      setOpen(false);
    };

    // Small delay to ensure dropdown is rendered
    const timer = setTimeout(() => {
      // Listen to all scroll events
      document.addEventListener("scroll", handleScroll, true);
      window.addEventListener("scroll", handleScroll, true);
      
      // Get all potentially scrollable parents
      const elementsToCleanup: HTMLElement[] = [];
      let element: HTMLElement | null = selectRef.current;
      
      while (element) {
        element.addEventListener("scroll", handleScroll, true);
        elementsToCleanup.push(element);
        element = element.parentElement;
      }

      // Store cleanup function
      cleanupFn = () => {
        document.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("scroll", handleScroll, true);
        elementsToCleanup.forEach(el => {
          el.removeEventListener("scroll", handleScroll, true);
        });
      };
    }, 10);

    return () => {
      clearTimeout(timer);
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [open]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    onChange(event.target.value as string | number);
  };

  const labelId = `select-options-label-${label
    ?.replace(/\s+/g, "-")
    .toLowerCase()}`;

  return (
    <div className="form-container" ref={selectRef}>
      <div>
        <label id={labelId} className={lableClass === "portal-text-lable" ? "portal-text-lable" : "text-lable"} htmlFor="custom-select">
          {label} {isMandatory && <span style={{ color: "red" }}>*</span>}
        </label>
      </div>

      <Select
        labelId={labelId}
        id="custom-select"
        error={error}
        value={value || ""}
        open={open}
        onChange={(e) => {
          handleChange(e);
          setOpen(false); // close on selection
        }}
        fullWidth
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disabled={disabled}
        size="small"
        displayEmpty
        renderValue={(selected: string | number | undefined) => {
          if (selected === "" || selected === undefined) {
            return (
              <span style={{ color: "#a5a3ae" }}>
                {placeholder || `Select ${label}`}
              </span>
            );
          }
          const match = options?.find(
            (opt) => String(opt.value) === String(selected)
          );
          // Show label for selected option; fallback to selected value string
          return (
            toDisplayText(match?.label) ||
            toDisplayText(match?.value) ||
            String(selected)
          ) as React.ReactNode;
        }}
        MenuProps={{
          disablePortal: false,
          disableScrollLock: true,
          autoFocus: false,
          PaperProps: {
            sx: {
              maxHeight: 250,
              zIndex: 1500,
            },
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
        }}
        sx={{
          height: "38px",
          borderRadius: "6px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: error ? "#d32f2f" : "rgba(0, 0, 0, 0.23)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: error ? "#d32f2f" : "#1CB1B1",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: error ? "#d32f2f" : "#1CB1B1",
          },
          "& .MuiSelect-select": {
            padding: "7px 14px 7px 14px !important",
            fontSize: "11px",
            color: "#a5a3ae",
            fontWeight: "400",
            textTransform: "capitalize",
          },
        }}
      >
        {options?.map((option, index) => (
          <MenuItem
            key={String(option?.value ?? index)}
            value={option?.value}
            className="disbursement-inside-page-lable"
            sx={{ textTransform: "capitalize !important" }}
          >
            {toDisplayText(option?.label) || toDisplayText(option?.value)}
          </MenuItem>
        ))}
      </Select>

      {/* Helper Text */}
      {helperText && (
        <FormHelperText
          error={error}
          style={{ marginTop: "4px", fontSize: "11px" }}
        >
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};

export default SelectOptions;
